import { Box } from '@chakra-ui/react';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { KeyboardEvent, ReactElement, useEffect, useId, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import VFormFieldHorizontal from '@voly/ui-components/src/v2/VFormFieldHorizontal';
import {
  DAY_IN_MILLISECONDS,
  HOUR_IN_MILLISECONDS,
  MINUTE_IN_MILLISECONDS,
} from 'src/app/core/helpers/date/date.consts';
import PointFieldName from './PointFieldName';
import FieldLockedIcon from '../FieldLockedIcon';
import UnitNumberInput from '../UnitNumberInput';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import VStack from '@voly/ui-components/src/v2/VStack.jsx';
import VInput from '@voly/ui-components/src/v2/VInput';
import VInputGroup from '@voly/ui-components/src/v2/VInputGroup';

const MAX_HOURS_IN_A_DAY = 23;
const MAX_MINUTES_IN_AN_HOUR = 59;
const WHOLE_AMOUNTS = 0;
const INPUT_SIZE = 'sm';

interface PointFieldTimeProps {
  field: TCustomField;
  value: string;
  readOnly: boolean;
  onSave: (value: string) => void;
}

type TTimeParts = {
  days: number;
  hours: number;
  minutes: number;
};

const toTotal = (milliseconds: string): number => {
  const total = Number(milliseconds);

  return Number.isFinite(total) ? total : 0;
};

const toTimeParts = (total: number, showHoursOnly: boolean): TTimeParts => {
  const magnitude = Math.abs(total);
  const days = showHoursOnly ? 0 : Math.floor(magnitude / DAY_IN_MILLISECONDS);

  return {
    days,
    hours: Math.floor((magnitude - days * DAY_IN_MILLISECONDS) / HOUR_IN_MILLISECONDS),
    minutes: Math.floor((magnitude % HOUR_IN_MILLISECONDS) / MINUTE_IN_MILLISECONDS),
  };
};

const toAmount = (value: number): number => (Number.isFinite(value) ? value : 0);

const toMilliseconds = ({ days, hours, minutes }: TTimeParts): string =>
  String(
    toAmount(days) * DAY_IN_MILLISECONDS +
      toAmount(hours) * HOUR_IN_MILLISECONDS +
      toAmount(minutes) * MINUTE_IN_MILLISECONDS,
  );

const toSummary = (total: number, showHoursOnly: boolean): string => {
  const { days, hours, minutes } = toTimeParts(total, showHoursOnly);

  const pluralValue = [
    { amount: days, one: 'day', many: 'days' },
    { amount: hours, one: 'hour', many: 'hours' },
    { amount: minutes, one: 'minute', many: 'minutes' },
  ]
    .filter((part) => part.amount > 0)
    .map((part) => `${part.amount} ${translate(part.amount === 1 ? part.one : part.many)}`);

  if (!pluralValue.length) {
    return `0 ${translate('minutes')}`;
  }

  const amounts = pluralValue.join(' ');

  return total < 0 ? `-${amounts}` : amounts;
};

const PointFieldTime = ({ field, value, readOnly, onSave }: PointFieldTimeProps): ReactElement => {
  const amountId = useId();
  const showHoursOnly = Boolean(field.showHoursOnly);
  const savedTotal = toTotal(value);
  const [daysValue, setDaysValue] = useState(0);
  const [hoursValue, setHoursValue] = useState(0);
  const [minuteValue, setMinuteValue] = useState(0);

  const [isLogging, setIsLogging] = useState(false);

  const canEdit = !readOnly && !field.lockedValue;
  const firstAmountId = showHoursOnly ? `${amountId}-hours` : `${amountId}-days`;

  useEffect(() => {
    if (!isLogging) {
      return;
    }

    document.getElementById(firstAmountId)?.focus();
  }, [isLogging, firstAmountId]);

  const commit = (): void => {
    setIsLogging(false);

    const milliseconds = toMilliseconds({
      days: daysValue,
      hours: hoursValue,
      minutes: minuteValue,
    });

    if (Number(milliseconds) === savedTotal) {
      return;
    }

    onSave(milliseconds);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!isLogging || event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    commit();
  };

  const handleIsLogging = () => {
    setIsLogging(true);
    setDaysValue(0);
    setHoursValue(0);
    setMinuteValue(0);
  };

  return (
    <VFormFieldHorizontal
      data-testid="point-custom-field-row"
      size="sm"
      label={<PointFieldName field={field} />}
      helperText={field.description}
      errorText={undefined}
      invalid={undefined}
      readOnly={readOnly}
      disabled={field.lockedValue}
    >
      <VTooltip content={translate('locked_by_admin')} disabled={!field.lockedValue}>
        <Box data-testid="point-custom-field-control" onKeyDown={handleKeyDown}>
          <VStack>
            <VInputGroup
              width="100%"
              startElement={field.lockedValue ? <FieldLockedIcon /> : undefined}
            >
              <VInput
                data-testid="point-custom-field-summary"
                width="100%"
                size={INPUT_SIZE}
                variant="subtle"
                value={toSummary(savedTotal, showHoursOnly)}
                readOnly
              />
            </VInputGroup>

            {isLogging && (
              <VStack key="logging-time" gap={2}>
                <VStackRow gap={2}>
                  {!showHoursOnly && (
                    <UnitNumberInput
                      value={daysValue}
                      min={-10000}
                      maxDecimalPlaces={WHOLE_AMOUNTS}
                      onValueChange={(amount) => setDaysValue(toAmount(amount))}
                      size={INPUT_SIZE}
                      ids={{ input: `${amountId}-days` }}
                      ariaLabel={translate('days')}
                      unit={translate('days_unit')}
                    />
                  )}

                  <UnitNumberInput
                    value={hoursValue}
                    min={-10000}
                    max={showHoursOnly ? undefined : MAX_HOURS_IN_A_DAY}
                    maxDecimalPlaces={WHOLE_AMOUNTS}
                    onValueChange={(amount) => setHoursValue(toAmount(amount))}
                    size={INPUT_SIZE}
                    ids={{ input: `${amountId}-hours` }}
                    ariaLabel={translate('hours')}
                    unit={translate('hours_unit')}
                  />

                  <UnitNumberInput
                    value={minuteValue}
                    min={-10000}
                    max={MAX_MINUTES_IN_AN_HOUR}
                    maxDecimalPlaces={WHOLE_AMOUNTS}
                    onValueChange={(amount) => setMinuteValue(toAmount(amount))}
                    size={INPUT_SIZE}
                    ids={{ input: `${amountId}-minutes` }}
                    ariaLabel={translate('minutes')}
                    unit={translate('minutes_unit')}
                  />
                </VStackRow>

                <VButton width="100%" onClick={commit}>
                  {translate('save')}
                </VButton>
              </VStack>
            )}

            {canEdit && !isLogging && (
              <VButton
                color="secondary"
                size="large"
                width="100%"
                onClick={() => handleIsLogging()}
              >
                {translate('log_time')}
              </VButton>
            )}
          </VStack>
        </Box>
      </VTooltip>
    </VFormFieldHorizontal>
  );
};

export default PointFieldTime;
